import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import IncludePropertyInEvents from "./include-feature-flag-property-in-backend-events.mdx";
import APIOverrideServerProperties from './override-server-properties/api.mdx';
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`There are 3 steps to implement feature flags using the PostHog API:`}</p>
    <h3 {...{
      "id": "step-1-evaluate-the-feature-flag-value-using-the-decide"
    }}>{`Step 1: Evaluate the feature flag value using the `}<inlineCode parentName="h3">{`/decide`}</inlineCode></h3>
    <p><inlineCode parentName="p">{`/decide`}</inlineCode>{` is the endpoint used to determine if a given flag is enabled for a certain user or not.`}</p>
    <h4 {...{
      "id": "request"
    }}>{`Request`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-shell"
      }}>{`curl -v -L --header "Content-Type: application/json" -d '  {
    "api_key": "<ph_project_api_key>",
    "distinct_id": "distinct_id_of_your_user",
    "groups" : { # Required only for group-based feature flags
        "group_type": "group_id" # Replace "group_type" with the name of your group type. Replace "group_id" with the id of your group.
    },
    "person_properties": {"<personProp1>": "<personVal1>"}, # Optional. Include any properties used to calculate the value of the feature flag.
    "group_properties": {"group type": {"<groupProp1>":"<groupVal1>"}} # Optional. Include any properties used to calculate the value of the feature flag.
}' https://app.posthog.com/decide?v=3 # or https://eu.posthog.com/decide?v=3
`}</code></pre>
    <blockquote>
      <p parentName="blockquote"><strong parentName="p">{`Note:`}</strong>{` `}<inlineCode parentName="p">{`person_properties`}</inlineCode>{` and `}<inlineCode parentName="p">{`group_properties`}</inlineCode>{` are optional. `}</p>
      <p parentName="blockquote">{`By default, flag evaluation uses the user and group properties stored in PostHog. You only need to provide `}<inlineCode parentName="p">{`person_properties`}</inlineCode>{` and `}<inlineCode parentName="p">{`group_properties`}</inlineCode>{` if you wish to use different values than the ones in PostHog.`}</p>
    </blockquote>
    <h4 {...{
      "id": "response"
    }}>{`Response`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-shell"
      }}>{`{
  "config": {
    "enable_collect_everything": true
  },
  "editorParams": {},
  "errorComputingFlags": false,
  "isAuthenticated": false,
  "supportedCompression": [
    "gzip",
    "lz64"
  ],
  "featureFlags": {
    "my-awesome-flag": true,
    "my-awesome-flag-2": true,
    "my-multivariate-flag": "some-string-value",
    "flag-thats-not-on": false,
  },
  "featureFlagPayloads": {
    "my-awesome-flag": "example-payload-string",
    "my-awesome-flag-2": "{\\"color\\": \\"blue\\", \\"animal\\": \\"hedgehog\\"}"
  }
}
`}</code></pre>
    <blockquote>
      <p parentName="blockquote"><strong parentName="p">{`Note:`}</strong>{` `}<inlineCode parentName="p">{`errorComputingFlags`}</inlineCode>{` will return `}<inlineCode parentName="p">{`true`}</inlineCode>{` if we didn't manage to compute some flags (for example, if there's an `}<a parentName="p" {...{
          "href": "https://status.posthog.com/"
        }}>{`ongoing incident involving flag evaluation`}</a>{`). `}</p>
      <p parentName="blockquote">{`This enables partial updates to currently active flags in your clients.`}</p>
    </blockquote>

    <IncludePropertyInEvents mdxType="IncludePropertyInEvents" />
    <p>{`To do this, include the `}<inlineCode parentName="p">{`$feature/feature_flag_name`}</inlineCode>{` property in your event:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-shell"
      }}>{`curl -v -L --header "Content-Type: application/json" -d '  {
    "api_key": "<ph_project_api_key>",
    "distinct_id": "distinct_id_of_your_user",
    "properties": {
        "$feature/feature-flag-key": "variant-key", # replace feature-flag-key with your flag key. Replace 'variant-key' with the key of your variant
    },
    "event": "your_event_name"
}' https://app.posthog.com/capture/ # or https://eu.posthog.com/capture/
`}</code></pre>
    <h3 {...{
      "id": "step-3-send-a-feature_flag_called-event"
    }}>{`Step 3: Send a `}<inlineCode parentName="h3">{`$feature_flag_called`}</inlineCode>{` event`}</h3>
    <p>{`To track usage of your feature flag and view related analytics in PostHog, submit the `}<inlineCode parentName="p">{`$feature_flag_called`}</inlineCode>{` event whenever you check a feature flag value in your code.`}</p>
    <p>{`You need to include two properties with this event:`}</p>
    <ol>
      <li parentName="ol"><inlineCode parentName="li">{`$feature_flag_response`}</inlineCode>{`: This is the name of the variant the user has been assigned to e.g., "control" or "test"`}</li>
      <li parentName="ol"><inlineCode parentName="li">{`$feature_flag`}</inlineCode>{`: This is the key of the feature flag in your experiment.`}</li>
    </ol>
    <pre><code parentName="pre" {...{
        "className": "language-shell"
      }}>{`curl -v -L --header "Content-Type: application/json" -d '  {
    "api_key": "<ph_project_api_key>",
    "distinct_id": "distinct_id_of_your_user",
    "properties": {
        "$feature_flag": "feature-flag-key",
        "$feature_flag_response": "variant-name"
    },
   "event": "$feature_flag_called"
}' https://app.posthog.com/capture/ # or https://eu.posthog.com/capture/
`}</code></pre>

    <APIOverrideServerProperties mdxType="APIOverrideServerProperties" />

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      